import SendbirdChat from '@sendbird/chat';
import { GroupChannelModule, SendbirdGroupChat } from '@sendbird/chat/groupChannel';
import type { ISendbirdConnectionCredentials } from '../../types/sendbirdTypes';

export type MarkUnreadOutcome =
  | 'marked'
  | 'already-unread'
  | 'already-read'
  | 'own-message'
  | 'no-messages'
  | 'failed'
  | 'unavailable';

export interface MarkUnreadResult {
  channelUrl: string;
  outcome: MarkUnreadOutcome;
  unreadMessageCount: number;
}

const markOne = async (sdk: SendbirdGroupChat, channelUrl: string, userId: string): Promise<MarkUnreadResult> => {
  try {
    const channel = await sdk.groupChannel.getChannel(channelUrl);
    const lastMessage = channel.lastMessage;
    if (!lastMessage) {
      return { channelUrl, outcome: 'no-messages', unreadMessageCount: 0 };
    }
    if (channel.unreadMessageCount > 0) {
      return { channelUrl, outcome: 'already-unread', unreadMessageCount: channel.unreadMessageCount };
    }
    const senderId = (lastMessage as { sender?: { userId?: string } }).sender?.userId;
    if (senderId === userId) {
      return { channelUrl, outcome: 'own-message', unreadMessageCount: 0 };
    }
    await channel.markAsUnread(lastMessage);
    const refreshed = await channel.refresh();
    return { channelUrl, outcome: 'marked', unreadMessageCount: Math.max(refreshed.unreadMessageCount, 1) };
  } catch {
    return { channelUrl, outcome: 'failed', unreadMessageCount: 0 };
  }
};

const markOneRead = async (sdk: SendbirdGroupChat, channelUrl: string): Promise<MarkUnreadResult> => {
  try {
    const channel = await sdk.groupChannel.getChannel(channelUrl);
    if (channel.unreadMessageCount === 0 && channel.unreadMentionCount === 0) {
      return { channelUrl, outcome: 'already-read', unreadMessageCount: 0 };
    }
    await channel.markAsRead();
    return { channelUrl, outcome: 'marked', unreadMessageCount: 0 };
  } catch {
    return { channelUrl, outcome: 'failed', unreadMessageCount: 0 };
  }
};

const runOnChannels = async (
  credentials: ISendbirdConnectionCredentials,
  channelUrls: string[],
  markOneChannel: (sdk: SendbirdGroupChat, channelUrl: string) => Promise<MarkUnreadResult>
): Promise<MarkUnreadResult[]> => {
  if (!channelUrls.length) return [];
  if (!SendbirdChat.instance) {
    return channelUrls.map((channelUrl) => ({ channelUrl, outcome: 'unavailable', unreadMessageCount: 0 }));
  }
  const sdk = SendbirdChat.init({
    appId: credentials.applicationId,
    modules: [new GroupChannelModule()],
    newInstance: true,
    localCacheEnabled: false,
  }) as SendbirdGroupChat;

  try {
    await sdk.connect(credentials.userId, credentials.accessToken);
  } catch {
    return channelUrls.map((channelUrl) => ({ channelUrl, outcome: 'failed', unreadMessageCount: 0 }));
  }

  try {
    const results: MarkUnreadResult[] = [];
    for (const channelUrl of channelUrls) {
      results.push(await markOneChannel(sdk, channelUrl));
    }
    return results;
  } finally {
    await sdk.disconnect().catch(() => undefined);
  }
};

export const markChannelsUnread = (credentials: ISendbirdConnectionCredentials, channelUrls: string[]) =>
  runOnChannels(credentials, channelUrls, (sdk, channelUrl) => markOne(sdk, channelUrl, credentials.userId));

export const markChannelsRead = (credentials: ISendbirdConnectionCredentials, channelUrls: string[]) =>
  runOnChannels(credentials, channelUrls, markOneRead);
